import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";

export default function AdminLeads() {
  const [leads, setLeads] = useState([]);
  const [active, setActive] = useState(null);
  const [statusFilter, setStatusFilter] = useState("");

  const load = useCallback(() => {
    return api
      .get("/leads", { params: { status: statusFilter || undefined } })
      .then(r => setLeads(r.data))
      .catch(() => {});
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/leads/${id}`, { status });
      toast.success("Updated");
      load();
      if (active?.id === id) setActive({ ...active, status });
    } catch (e) { toast.error("Could not update"); }
  };

  return (
    <div data-testid="admin-leads">
      <div className="eyebrow text-solar mb-2">Pipeline</div>
      <h1 className="mb-2 text-3xl">Leads</h1>
      <p className="text-white/60 text-sm mb-6">Quote and contact submissions.</p>

      <div className="flex gap-2 mb-4">
        {["", "new", "contacted", "qualified", "closed"].map(s => (
          <button key={s || "all"} data-testid={`filter-${s || "all"}`} onClick={() => setStatusFilter(s)}
            className={`px-3 py-1.5 text-xs uppercase tracking-wider font-bold border ${statusFilter === s ? "bg-solar text-black border-solar" : "border-chrome text-white/75 hover:border-solar"}`}>
            {s || "All"}
          </button>
        ))}
      </div>

      <div className="grid lg:grid-cols-[1.5fr_1fr] gap-6">
        <div className="border border-chrome bg-steel overflow-x-auto">
          <table className="w-full min-w-[720px]">
            <thead>
              <tr className="border-b border-chrome text-left">
                <th className="p-3 text-[11px] uppercase tracking-widest text-white/60">Name</th>
                <th className="p-3 text-[11px] uppercase tracking-widest text-white/60">Site</th>
                <th className="p-3 text-[11px] uppercase tracking-widest text-white/60">Location</th>
                <th className="p-3 text-[11px] uppercase tracking-widest text-white/60">Status</th>
                <th className="p-3 text-[11px] uppercase tracking-widest text-white/60">Date</th>
              </tr>
            </thead>
            <tbody>
              {leads.map(l => (
                <tr key={l.id} data-testid={`lead-row-${l.id}`} onClick={() => setActive(l)} className={`border-b border-chrome cursor-pointer hover:bg-obsidian ${active?.id === l.id ? "bg-obsidian" : ""}`}>
                  <td className="p-3">
                    <div className="font-semibold text-sm">{l.first_name} {l.last_name}</div>
                    <div className="text-xs text-white/60">{l.email}</div>
                  </td>
                  <td className="p-3 text-sm text-white/85">{l.site_type}</td>
                  <td className="p-3 text-sm text-white/85">{l.city}, {l.state}</td>
                  <td className="p-3"><StatusPill status={l.status} /></td>
                  <td className="p-3 text-xs text-white/60">{(l.created_at || "").substring(0, 10)}</td>
                </tr>
              ))}
              {leads.length === 0 && <tr><td colSpan="5" className="p-6 text-center text-white/60 text-sm">No leads.</td></tr>}
            </tbody>
          </table>
        </div>

        <div className="border border-chrome bg-steel p-6 lg:sticky lg:top-6 self-start">
          {active ? (
            <div data-testid="lead-detail">
              <div className="eyebrow text-solar mb-2">Lead detail</div>
              <div className="font-display uppercase text-2xl font-bold mb-1">{active.first_name} {active.last_name}</div>
              <div className="text-white/70 text-sm mb-4">{active.company}</div>
              <div className="space-y-2 text-sm">
                <Row label="Email" value={<a className="text-solar" href={`mailto:${active.email}`}>{active.email}</a>} />
                <Row label="Phone" value={<a className="text-solar" href={`tel:${active.phone}`}>{active.phone}</a>} />
                <Row label="City / State" value={`${active.city}, ${active.state}`} />
                <Row label="Site type" value={active.site_type} />
                <Row label="Preferred system" value={active.preferred_system} />
                <Row label="Rental duration" value={active.rental_duration} />
                <Row label="Monitoring" value={active.monitoring_preference} />
                <Row label="Deployment date" value={active.deployment_date} />
                <Row label="Security concern" value={active.security_concern} />
                <Row label="Details" value={active.details} />
                <Row label="Referral" value={active.referral_source} />
              </div>
              <div className="mt-6 pt-6 border-t border-chrome">
                <div className="eyebrow text-solar mb-2">Update status</div>
                <div className="flex flex-wrap gap-2">
                  {["new", "contacted", "qualified", "closed"].map(s => (
                    <button key={s} data-testid={`set-status-${s}`} onClick={() => updateStatus(active.id, s)} className={`px-3 py-1.5 text-xs uppercase tracking-wider font-bold border ${active.status === s ? "bg-solar text-black border-solar" : "border-chrome text-white/75 hover:border-solar"}`}>{s}</button>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="text-white/60 text-sm">Select a lead to view details.</div>
          )}
        </div>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  if (!value) return null;
  return (
    <div className="grid grid-cols-[130px_1fr] gap-3 border-b border-chrome pb-2">
      <div className="eyebrow text-white/50 text-[10px]">{label}</div>
      <div className="text-white/85 break-words">{value}</div>
    </div>
  );
}
function StatusPill({ status }) {
  const map = { new: "bg-solar text-black", contacted: "bg-blue-500 text-white", qualified: "bg-emerald-500 text-black", closed: "bg-white/20 text-white/70" };
  return <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 ${map[status] || "bg-white/10 text-white/70"}`}>{status || "new"}</span>;
}
